import Background from "../../../components/Background";
import Topnav from "../../../components/Topnav";
import { Box, Typography, Button, ToggleButton } from "@mui/material";
import { useDispatch } from "react-redux";
import { useState } from "react";
import CopyRightTextMain from "../../../components/CopyRightTextMain";
import { useNavigate } from "react-router-dom";
import { updateFields } from "../../../redux/profileSlice";
import RoundedTextField from "../../../components/RoundedProfileTextField";
import { useEditableProfile } from "../../../hooks/useEditableProfile";

const EditDisabilityStatus = () => {
  // Router state when opened from the Profile UI, the stored profile on a
  // reload or a pasted link (ECH-387). `RequireProfileLoaded` guarantees one
  // of them exists before this component mounts, so the initialisers below
  // are safe to dereference.
  const { profile: localProfile } = useEditableProfile();
  const demographics = localProfile?.demographics ?? {};
  const [selectedDisabilityStatus, setSelectedDisabilityStatus] = useState(
    demographics.disabilityStatuses?.[0],
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [disabilityDescription, setDisabilityDescription] = useState(
    localProfile?.profile?.disabilityDescription ?? "",
  );

  const handleChange = (value) => {
    setSelectedDisabilityStatus(value);
    if (value === "no") {
      setDisabilityDescription("");
    }
    dispatch(
      updateFields({
        field: "demographics.disabilityStatuses",
        value: [value],
      }),
    );
  };

  if (!localProfile) {
    return <div>No profile data found.</div>;
  }
  return (
    <Background>
      <Topnav />
      <Box className="selection-box">
        <Typography
          className="text-primary"
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: {
              xs: "18px",
              sm: "20px",
              md: "24px",
              lg: "26px",
              xl: "32px",
            },
            marginBottom: "5px",
          }}
        >
          Disability
        </Typography>
        <Typography
          variant="body"
          sx={{
            fontWeight: 400,
            fontSize: {
              xs: "12px",
              sm: "13px",
              md: "14px",
              lg: "16px",
            },
            color: "#152134",
            marginBottom: "20px",
          }}
        >
          This information is <b>private</b> and will{" "}
          <b>never be shared or displayed</b> on your profile. It is collected
          <b> for demographic measurement purposes only</b> to help us better
          serve our users and promote equity within our community.{" "}
        </Typography>
        <ToggleButton
          selected={selectedDisabilityStatus === "yes"}
          onChange={() => handleChange("yes")}
          sx={{
            textTransform: "none",
            borderRadius: "50px",
            width: "100%",
            marginTop: "10px",
            background:
              selectedDisabilityStatus === "yes"
                ? "#7BB9D5 !important"
                : "#ffffff",
            borderColor:
              selectedDisabilityStatus === "yes" ? "#7BB9D5" : "#AAB6C8",
            fontWeight: selectedDisabilityStatus === "yes" ? 600 : 400,
            height: {
              xs: "40px",
              sm: "45px",
              md: "45px",
              lg: "50px",
              xl: "66px",
            },
            color: "#2A4876 !important",
            fontSize: {
              xs: "14px",
              sm: "15px",
              md: "16px",
              lg: "16px",
              xl: "20px",
            },
            marginBottom: "20px",
            "&:hover": {
              backgroundColor: "#7BB9D5 !important",
              borderColor: "#7BB9D5 !important",
            },
          }}
        >
          Yes, I have a disability
        </ToggleButton>
        <ToggleButton
          selected={selectedDisabilityStatus === "no"}
          onChange={() => handleChange("no")}
          sx={{
            textTransform: "none",
            borderRadius: "50px",
            width: "100%",
            background:
              selectedDisabilityStatus === "no"
                ? "#7BB9D5 !important"
                : "#ffffff",
            borderColor:
              selectedDisabilityStatus === "no" ? "#7BB9D5" : "#AAB6C8",
            fontWeight: selectedDisabilityStatus === "no" ? 600 : 400,
            height: {
              xs: "40px",
              sm: "45px",
              md: "45px",
              lg: "50px",
              xl: "66px",
            },
            color: "#2A4876 !important",
            fontSize: {
              xs: "14px",
              sm: "15px",
              md: "16px",
              lg: "16px",
              xl: "20px",
            },
            "&:hover": {
              backgroundColor: "#7BB9D5 !important",
              borderColor: "#7BB9D5 !important",
            },
          }}
        >
          No, I do not have a disability
        </ToggleButton>
        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "100%",
              md: "100%",
              lg: "100%",
            },
            margin: "auto",
          }}
        >
          {selectedDisabilityStatus === "yes" && (
            <Box
              sx={{
                marginTop: {
                  xs: "20px",
                  sm: "20px",
                  md: "20px",
                  lg: "20px",
                  xl: "30px",
                },
              }}
            >
              <Typography
                sx={{
                  textAlign: "left",
                  fontSize: {
                    xs: "12px",
                    sm: "13px",
                    md: "14px",
                  },
                  fontWeight: 400,
                  marginBottom: "5px",
                  color: "#152134",
                }}
              >
                <span style={{ fontWeight: "bold", fontSize: "15px" }}>
                  Optional: Describe your disability
                </span>
                <br />
              </Typography>

              <RoundedTextField
                name="disabilityDescription"
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                value={disabilityDescription}
                onChange={(e) => {
                  setDisabilityDescription(e.target.value);
                  dispatch(
                    updateFields({
                      field: "disabilityDescription",
                      value: e.target.value,
                    }),
                  );
                }}
                slotProps={{
                  input: {
                    "aria-label": "Describe your disability",
                  },
                }}
              />
            </Box>
          )}
        </Box>
        <Button
          variant="contained"
          fullWidth
          className="Send-button"
          disabled={!selectedDisabilityStatus}
          sx={{
            backgroundColor: selectedDisabilityStatus ? "#2A4876" : "#AAB6C8", // Change color based on input values
            cursor: selectedDisabilityStatus ? "pointer" : "not-allowed", // Disable hover effect when fields are not filled
            color: "#fff",
            marginTop: "20px !important",
          }}
          onClick={() => {
            navigate("/profile/edit-profile");
          }}
        >
          Save
        </Button>

        <Typography
          variant="body2"
          style={{
            cursor: "pointer",
          }}
          onClick={() => {
            handleChange("prefer not to say");
            navigate("/profile/edit-profile");
          }}
          sx={{
            color: "#152134",
            fontSize: "14px",
            marginTop: {
              xs: "10px",
              sm: "10px",
              md: "10px",
              lg: "20px",
              xl: "20px",
            },
          }}
        >
          Prefer Not to say
        </Typography>
      </Box>
      <CopyRightTextMain />
    </Background>
  );
};

export default EditDisabilityStatus;
